import { round } from '../../../utils/typeUtils'
import { AdjustmentNote, AdjustmentNoteKind, AdjustmentNoteType } from '../state/types'

export const MANUAL_ADJUSTMENT_ITEM_NAME = 'Ajuste de precio'

// Price adjustment (not from cancelled or returned items). Same rule as the server: the notes created before `kind`
// are recognized by the `manual` flag or by the name of their items
export const isManualCreditNote = (adjustmentNote: AdjustmentNote): boolean =>
    adjustmentNote.type === AdjustmentNoteType.CREDIT &&
    (
        adjustmentNote.kind === AdjustmentNoteKind.MANUAL ||
        (!adjustmentNote.kind && (!!adjustmentNote.manual || adjustmentNote.items.every(item => item.name.startsWith(MANUAL_ADJUSTMENT_ITEM_NAME))))
    )

// How the amount of a manual credit note is decided
export enum ManualCreditNoteMode {
    // An amount spread over all the items by their total: a discount on the whole operation
    PRORATED = 'Prorated',
    // The correct unit price of some products: the price of those products was wrong
    PRODUCTS = 'Products'
}

export type ManualCreditNoteBalances = {
    // Already invoiced that can still be credited by a fiscal note (without perceptions)
    creditableAmount: number
    // Adjusted total still to be invoiced
    remainingAmount: number
}

export type ManualCreditNoteSplit = {
    fiscalAmount: number
    internalAmount: number
}

// An operation item a manual credit note can discount
export type ManualCreditNoteLine = {
    itemId: string
    // Sold minus cancelled or returned
    activeQuantity: number
    // Of the active units, the ones already invoiced
    invoicedQuantity: number
    // Unit price with VAT and discounts, without the payment type surcharge and without the previous manual discounts
    unitPrice: number
    vatTaxYield: number
}

// Discount on some units of an item. The unit amount is in the note space: with VAT and the payment type surcharge
export type ManualAdjustmentPart = {
    itemId: string
    quantity: number
    unitAmount: number
    vatTaxYield: number
    // Rounded total when it can't be the unit amount times the quantity (the prorated or split amounts keep their sum)
    total?: number
}

export type ManualAdjustmentSplit = {
    fiscal: ManualAdjustmentPart[]
    internal: ManualAdjustmentPart[]
}

// What the user asked for: an amount to prorate, or the correct unit price of some items (by item id)
export type ManualCreditNoteRequest = {
    mode: ManualCreditNoteMode
    amount?: number
    correctPrices?: Record<string, number>
}

export type ManualCreditNoteData = {
    balances: ManualCreditNoteBalances
    // Empty in a financial hoard: it has no items when the note is created
    lines: ManualCreditNoteLine[]
    surchargeFactor: number
}

// What the server saved
export type ManualCreditNoteResult = {
    adjustmentNotes: AdjustmentNote[]
    // What the customer had paid above the new total: it went to their credit in favor
    creditInFavor: number
}

const AMOUNT_TOLERANCE = 0.01
const QUANTITY_TOLERANCE = 0.000001

const lineTotal = (line: ManualCreditNoteLine, surchargeFactor: number) => line.unitPrice * surchargeFactor * line.activeQuantity

const activeLines = (lines: ManualCreditNoteLine[]) => lines.filter(line => line.activeQuantity > QUANTITY_TOLERANCE && line.unitPrice > 0)

// What the note can credit at most: the active units at their current price
export const getManualCreditNoteMaxAmount = (lines: ManualCreditNoteLine[], surchargeFactor: number): number =>
    round(activeLines(lines).reduce((sum, line) => sum + lineTotal(line, surchargeFactor), 0))

// The amount is spread over the items by their total, and over the units of each item
export const prorateManualAdjustment = (
    amount: number,
    lines: ManualCreditNoteLine[],
    surchargeFactor: number
): ManualAdjustmentPart[] => {
    const prorated = activeLines(lines)
    const base = prorated.reduce((sum, line) => sum + lineTotal(line, surchargeFactor), 0)
    if (amount <= AMOUNT_TOLERANCE || base <= AMOUNT_TOLERANCE) return []

    // The last item takes the rounding difference, so the note credits exactly the amount
    let pendingAmount = round(amount)
    return prorated.map((line, index) => {
        const isLast = index === prorated.length - 1
        const total = isLast ? pendingAmount : round(amount * lineTotal(line, surchargeFactor) / base)
        pendingAmount = round(pendingAmount - total)
        return {
            itemId: line.itemId,
            quantity: line.activeQuantity,
            unitAmount: total / line.activeQuantity,
            vatTaxYield: line.vatTaxYield,
            total
        }
    })
}

export const getPartTotal = (part: ManualAdjustmentPart): number => part.total ?? round(part.unitAmount * part.quantity)

// Each corrected product is discounted by the difference with its correct price, on all its active units
export const correctManualAdjustmentPrices = (
    correctPrices: Map<string, number>,
    lines: ManualCreditNoteLine[],
    surchargeFactor: number
): ManualAdjustmentPart[] => activeLines(lines)
    .filter(line => correctPrices.has(line.itemId))
    .map(line => ({
        itemId: line.itemId,
        quantity: line.activeQuantity,
        unitAmount: (line.unitPrice - correctPrices.get(line.itemId)!) * surchargeFactor,
        vatTaxYield: line.vatTaxYield
    }))
    .filter(part => part.unitAmount > 0)

// The invoiced units go in the fiscal note and the rest in the internal one (the next invoices discount it)
export const splitManualAdjustment = (
    parts: ManualAdjustmentPart[],
    lines: ManualCreditNoteLine[]
): ManualAdjustmentSplit => {
    const fiscal: ManualAdjustmentPart[] = []
    const internal: ManualAdjustmentPart[] = []
    parts.forEach(part => {
        const line = lines.find(line => line.itemId === part.itemId)
        const invoicedQuantity = Math.min(part.quantity, line?.invoicedQuantity || 0)
        const uninvoicedQuantity = part.quantity - invoicedQuantity
        // The internal part takes the rounding difference, so both parts add up to the part total
        const fiscalTotal = uninvoicedQuantity > QUANTITY_TOLERANCE ? round(part.unitAmount * invoicedQuantity) : getPartTotal(part)
        if (invoicedQuantity > QUANTITY_TOLERANCE) fiscal.push({ ...part, quantity: invoicedQuantity, total: fiscalTotal })
        if (uninvoicedQuantity > QUANTITY_TOLERANCE) {
            internal.push({ ...part, quantity: uninvoicedQuantity, total: round(getPartTotal(part) - (invoicedQuantity > QUANTITY_TOLERANCE ? fiscalTotal : 0)) })
        }
    })
    return { fiscal, internal }
}

// What a returned or cancelled unit credits: its price minus the manual discounts it already got. The discounts of the
// notes created before they were per item (legacyFactor) are taken as prorated over the whole operation
export const getNetUnitTotal = (unitTotal: number, manualUnitDiscount = 0, legacyFactor = 0): number =>
    Math.max(0, unitTotal - manualUnitDiscount - unitTotal * legacyFactor)

export const getManualAdjustmentAmount = (parts: ManualAdjustmentPart[]): number =>
    round(parts.reduce((sum, part) => sum + getPartTotal(part), 0))

// A financial hoard has no items when the note is created: the amount is split between what is invoiced and what is
// left to invoice in the same proportion. The fiscal part never goes over what is invoiced and creditable
export const splitManualCreditNote = (
    amount: number,
    balances: ManualCreditNoteBalances
): ManualCreditNoteSplit => {
    const { creditableAmount, remainingAmount } = balances
    const base = creditableAmount + remainingAmount
    let fiscalAmount = base > AMOUNT_TOLERANCE ? amount * creditableAmount / base : 0
    fiscalAmount = round(Math.min(Math.max(0, fiscalAmount), creditableAmount, amount))
    if (fiscalAmount <= AMOUNT_TOLERANCE) fiscalAmount = 0

    const internalAmount = round(amount - fiscalAmount)
    return {
        fiscalAmount,
        internalAmount: internalAmount > AMOUNT_TOLERANCE ? internalAmount : 0
    }
}
